'use server'

import { apiRequest, ApiResponse } from '../request'

// ==========================================
// INTERFACES
// ==========================================

export type InternalConversationType = 'PRIVATE' | 'BROADCAST_TYPE' | 'BROADCAST_ALL' | 'AI_CHAT'
export type InternalMessageStatus =
    | 'SENT'
    | 'DELIVERED'
    | 'PARTIALLY_READ'
    | 'READ'
export type TargetUserType = 'COMPANY' | 'EMPLOYEE' | 'PROFESSIONAL'

export interface InternalChatUser {
    id: number
    name: string
    email?: string
    picture?: string
    userType: string
}

export interface MessageReply {
    id: number
    content: string
    senderName?: string
    isAiMessage: boolean
}

export interface InternalMessage {
    id: number
    conversationId: number
    senderId?: number
    sender?: InternalChatUser
    isAiMessage: boolean
    content: string
    replyTo?: MessageReply
    status: InternalMessageStatus
    attachments?: string[]
    createdAt: string
    readCount?: number
    totalRecipients?: number
}

export interface InternalConversation {
    id: number
    type: InternalConversationType
    title?: string
    lastMessageAt?: string
    lastMessagePreview?: string
    participants?: InternalChatUser[]
    targetUserType?: string
    unreadCount: number
    createdAt: string
}

export interface ConversationListResponse {
    conversations: InternalConversation[]
    total: number
    page: number
    limit: number
}

export interface MessageListResponse {
    messages: InternalMessage[]
    total: number
    page: number
    limit: number
}

export interface CreateConversationData {
    type: InternalConversationType
    targetUserId?: number
    targetUserType?: TargetUserType
    title?: string
}

export interface SendMessageData {
    content: string
    replyToId?: number
    attachments?: string[]
}

export interface MessageReadDetails {
    totalRecipients: number
    readCount: number
    readBy: { userId: number; userName: string; readAt: string }[]
}

export interface UploadedFile {
    filename: string
    originalname: string
    url: string
    mimetype: string
    size: number
}

export interface UploadResponse {
    success: boolean
    files: UploadedFile[]
}

// ==========================================
// API FUNCTIONS
// ==========================================

export async function getConversations(
    page: number = 1,
    limit: number = 20
): Promise<ApiResponse<ConversationListResponse>> {
    return apiRequest('internal-chat/conversations', { params: { page, limit } })
}

export async function getConversation(
    id: number
): Promise<ApiResponse<InternalConversation>> {
    return apiRequest(`internal-chat/conversations/${id}`, {})
}

export async function getMessages(
    conversationId: number,
    page: number = 1,
    limit: number = 50
): Promise<ApiResponse<MessageListResponse>> {
    return apiRequest(`internal-chat/conversations/${conversationId}/messages`, {
        params: { page, limit },
    })
}

export async function createConversation(
    data: CreateConversationData
): Promise<ApiResponse<InternalConversation>> {
    return apiRequest('internal-chat/conversations', { data }, 'post')
}

export async function sendMessage(
    conversationId: number,
    data: SendMessageData
): Promise<ApiResponse<InternalMessage>> {
    return apiRequest(
        `internal-chat/conversations/${conversationId}/messages`,
        { data },
        'post'
    )
}

export async function markAsRead(
    conversationId: number
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(
        `internal-chat/conversations/${conversationId}/read`,
        {},
        'patch'
    )
}

export async function getUnreadCount(): Promise<
    ApiResponse<{ count: number }>
> {
    return apiRequest('internal-chat/unread-count', {})
}

export async function getAvailableUsers(): Promise<
    ApiResponse<InternalChatUser[]>
> {
    return apiRequest('internal-chat/users', {})
}

export async function getMessageReadDetails(
    messageId: number
): Promise<ApiResponse<MessageReadDetails>> {
    return apiRequest(`internal-chat/messages/${messageId}/read-details`, {})
}

export async function uploadFiles(
    files: File[]
): Promise<ApiResponse<UploadResponse>> {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    return apiRequest('internal-chat/upload', { data: formData, formData: true }, 'post')
}
